import { z } from "zod";

import type {
  MediaLibraryAssetItem,
  MediaLibraryListResponse,
} from "@/types/catalogAssets";

const nullableInt = z.number().int().nullish().default(null);
const emptyString = z
  .string()
  .nullish()
  .transform((v) => v ?? "");

const rawAssetSchema = z
  .object({
    id: z.number().int(),
    handle: emptyString,
    url: emptyString,
    title: emptyString,
    type: z.string().nullish().default(""),
    alt: emptyString,
    origin: z.enum(["SOCIAL", "PDP_ALT", "PDP_HERO"]).nullish(),
    width_px: nullableInt,
    height_px: nullableInt,
    file_size: nullableInt,
    file_name: emptyString,
    mime_type: z.string().nullish().default(null),
    upload_status: z.string().optional().default("uploaded"),
    approval_status: z
      .enum(["approved", "draft"])
      .optional()
      .default("approved"),
    source: z.string().optional().default("ingested"),
    product_id: emptyString,
    product_title: emptyString,
    product_handle: emptyString,
    variant_id: emptyString,
  })
  .passthrough();

function toMediaItem(
  raw: z.infer<typeof rawAssetSchema>,
): MediaLibraryAssetItem | null {
  if (raw.type !== "image" && raw.type !== "video" && raw.type !== "document") {
    return null;
  }
  return {
    id: raw.id,
    handle: raw.handle,
    url: raw.url,
    title: raw.title,
    type: raw.type,
    alt: raw.alt,
    origin: raw.origin ?? null,
    width_px: raw.width_px,
    height_px: raw.height_px,
    file_size: raw.file_size,
    file_name: raw.file_name,
    mime_type: raw.mime_type,
    upload_status: raw.upload_status,
    approval_status: raw.approval_status,
    source: raw.source,
    product_id: raw.product_id,
    product_title: raw.product_title,
    product_handle: raw.product_handle,
    variant_id: raw.variant_id,
  };
}

export const mediaLibraryListResponseSchema = z
  .object({
    assets: z.array(rawAssetSchema).default([]),
    next: z.number().int().nullish().default(null),
    count: z.number().int().nullish().default(null),
  })
  .passthrough()
  .transform(
    (raw): MediaLibraryListResponse => ({
      assets: raw.assets
        .map(toMediaItem)
        .filter((item): item is MediaLibraryAssetItem => item !== null),
      next: raw.next,
      count: raw.count,
    }),
  );
